///
/// EarthBrowser
///
/// Created by Matt Giger on 10/26/2026
/// Copyright (c) 2026 Geodata Labs
///
/// A section strip laid on a storm column wall: the area between the base
/// line and a profile (cloud-top height through the center, modeled wind).
/// Built in a local frame along one block axis; the parent translates it
/// onto whichever wall is far from the camera. Rebuilt when the profile
/// changes (a new IR frame), which is a few hundred vertices.
///

import { NodeBase } from "@stovokor/engine/NodeBase"
import { Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type color } from "@stovokor/math/color"
import { type cube } from "@stovokor/math/cube"
import { type matrix } from "@stovokor/math/matrix"
import { type sphere } from "@stovokor/math/sphere"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { type Material } from "@stovokor/shader/Material"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"

export type SectionAxis = "x" | "z"

export class StormSectionNode extends NodeBase {
	private readonly sectionContext: StovokorContext
	private readonly color: color
	private opacityValue = 0
	private strip?: StormSectionRenderer

	constructor(context: StovokorContext, color: color) {
		super()
		this.sectionContext = context
		this.color = color
		this.renderOrder = 1
	}

	/**
	 * Replace the profile: `along` are positions on the axis (km) and
	 * `heights` the frame-km heights above the base line, same length
	 */
	public setProfile(axis: SectionAxis, along: number[], heights: number[]): void {
		this.renderer?.destroy()
		this.strip = undefined
		if (along.length < 2) {
			this.renderer = undefined
			return
		}
		const geometry = new StormSectionGeometry(this.sectionContext, axis, along, heights)
		this.strip = new StormSectionRenderer(this.sectionContext, geometry)
		this.strip.color.value = this.color
		this.strip.opacity.value = this.opacityValue
		this.renderer = this.strip
	}

	public set opacity(value: number) {
		this.opacityValue = value
		if (this.strip) this.strip.opacity.value = value
	}
}

class StormSectionRenderer extends Renderer {
	public modelViewProjection = new UniformDynamic<matrix>()
	public color = new UniformDynamic<color>()
	public opacity = new UniformDynamic<number>(0)

	constructor(context: StovokorContext, geometry: Geometry) {
		const transparentMaterial: Material = { transparency: true, available: true }
		super(context, new ShaderSourceDefinition("storm_column_section"), geometry, [transparentMaterial])
		this.drawState.cullFace = false
		this.drawState.depthWrite = false
	}

	protected stateUpdate(state: RenderState): void {
		this.modelViewProjection.value = state.modelViewProjection
	}
}

class StormSectionGeometry implements Geometry {
	public position: VertexAttribute
	public edge: VertexAttribute
	public indexBuffer: IndexBuffer
	public vertexBuffer: VertexBuffer
	public mode: DrawMode = DrawMode.Triangles
	public bounds: cube
	public boundingSphere: sphere
	public available: boolean = true

	constructor(context: StovokorContext, axis: SectionAxis, along: number[], heights: number[]) {
		this.position = new VertexAttribute(context, 3)
		this.edge = new VertexAttribute(context, 1)
		this.indexBuffer = new IndexBuffer(context)
		this.vertexBuffer = new VertexBuffer(context, [this.position, this.edge])

		for (let i = 0; i < along.length; i++) {
			const a = along[i]
			const h = Math.max(heights[i], 0)
			if (axis === "x") {
				this.position.addPos(a, 0, 0)
				this.position.addPos(a, h, 0)
			} else {
				this.position.addPos(0, 0, a)
				this.position.addPos(0, h, a)
			}
			this.edge.add(0)
			this.edge.add(1)
		}
		for (let i = 0; i < along.length - 1; i++) {
			const b0 = i * 2
			const t0 = b0 + 1
			const b1 = b0 + 2
			const t1 = b0 + 3
			this.indexBuffer.quad(b0, b1, t1, t0)
		}
		this.bounds = this.position.bounds
		this.boundingSphere = this.bounds.sphere
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.indexBuffer.destroy()
	}
}
